import React, { useEffect, useState } from 'react';
import { AlertTriangle, Package, RefreshCw } from 'lucide-react';
import { getStagingCount } from '@/services/stagingApi';

/**
 * StagingAlert Component
 * Displays a badge/alert showing USNs waiting in staging area
 * 
 * @param {string} section - Section identifier (greige-storage, processing, inhouse, dispatch)
 * @param {function} onNavigate - Optional callback when alert is clicked
 */
const StagingAlert = ({ section, onNavigate }) => {
  const [stagingData, setStagingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStagingCount = async () => {
    try {
      setLoading(true);
      const data = await getStagingCount(section);
      setStagingData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching staging count:', err);
      setError('Failed to load staging data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStagingCount();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchStagingCount, 30000);
    return () => clearInterval(interval);
  }, [section]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-100 rounded-lg animate-pulse">
        <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
        <span className="text-sm text-gray-500">Loading staging data...</span>
      </div>
    );
  }

  if (error || !stagingData) {
    return null;
  }

  if (!stagingData.has_staging) {
    return null; // Don't show anything if no staging items
  }

  return (
    <div 
      onClick={onNavigate}
      className={`
        flex items-center justify-between gap-4 px-4 py-3 rounded-lg border-2 
        bg-amber-50 border-amber-300 hover:border-amber-400 
        cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md
      `}
      data-testid="staging-alert"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
          <AlertTriangle className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-sm font-semibold text-amber-800">
            Staging Alert
          </p>
          <p className="text-xs text-amber-600">
            Items waiting for Bundle Receipt
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="text-center">
          <p className="text-2xl font-bold text-amber-700">
            {stagingData.staging_usn_count}
          </p>
          <p className="text-xs text-amber-600">USNs</p>
        </div>
        <div className="text-center border-l border-amber-300 pl-4">
          <p className="text-2xl font-bold text-amber-700">
            {stagingData.pending_bundle_count}
          </p>
          <p className="text-xs text-amber-600">Bundles</p>
        </div>
      </div>
    </div>
  );
};

export default StagingAlert;
